import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, ArrowRight, Shield } from "lucide-react";

const severityConfig = {
  critical: { bg: "bg-red-50", border: "border-red-200", badge: "bg-red-100 text-red-800", dot: "bg-red-500" },
  high: { bg: "bg-orange-50", border: "border-orange-200", badge: "bg-orange-100 text-orange-800", dot: "bg-orange-500" },
  medium: { bg: "bg-amber-50", border: "border-amber-200", badge: "bg-amber-100 text-amber-800", dot: "bg-amber-500" },
  low: { bg: "bg-blue-50", border: "border-blue-200", badge: "bg-blue-100 text-blue-800", dot: "bg-blue-500" },
};

const RiskMatrix = ({ risks }) => {
  if (!risks || risks.length === 0) {
    return (
      <Card className="card-industrial">
        <CardContent className="p-8 text-center">
          <Shield className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
          <h3 className="font-medium text-base">No Active Risks</h3>
          <p className="text-sm text-muted-foreground">All cross-functional metrics are within acceptable ranges.</p>
        </CardContent>
      </Card>
    );
  }

  const grouped = {
    critical: risks.filter((r) => r.severity === "critical"),
    high: risks.filter((r) => r.severity === "high"),
    medium: risks.filter((r) => r.severity === "medium"),
  };

  return (
    <div className="space-y-4" data-testid="risk-matrix">
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500" /> Critical</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-orange-500" /> High</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500" /> Medium</span>
      </div>

      {risks.map((risk, idx) => {
        const cfg = severityConfig[risk.severity] || severityConfig.medium;
        return (
          <Card key={idx} className={`card-industrial ${cfg.border} ${cfg.bg}`} data-testid={`risk-item-${idx}`}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <span className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${cfg.dot}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h4 className="font-medium text-sm">{risk.title}</h4>
                    <Badge className={`text-[10px] ${cfg.badge}`}>{risk.severity.toUpperCase()}</Badge>
                    <Badge variant="outline" className="text-[10px]">{risk.category}</Badge>
                  </div>

                  <p className="text-xs text-muted-foreground mb-2">
                    <span className="font-medium">Impact:</span> {risk.impact}
                  </p>

                  <div className="flex items-center gap-1.5 mb-2">
                    <span className="text-[10px] text-muted-foreground">Affects:</span>
                    {risk.modules.map((m) => (
                      <Badge key={m} variant="secondary" className="text-[10px] h-5">
                        {m}
                      </Badge>
                    ))}
                  </div>

                  <div className="flex items-start gap-1.5 bg-white/60 rounded p-2">
                    <ArrowRight className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" />
                    <p className="text-xs">{risk.recommendation}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default RiskMatrix;
